/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation';

import type { InlineCommentMarkerRefLocation } from '@confluence/inline-comments-queries/entry-points/InlineCommentMarkerRefQuery.ts';
import {
	handleDeepLinkNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useCommentsPanelActions,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';

import {
	GraphQLContentStatus,
	type useFocusedCommentInlineCommentMarkerRefQuery as useFocusedCommentInlineCommentMarkerRefQuery$data,
	type useFocusedCommentInlineCommentMarkerRefQueryVariables as useFocusedCommentInlineCommentMarkerRefQuery$variables,
} from './__types__/useFocusedCommentInlineCommentMarkerRefQuery.ts';

type FocusedCommentProps = {
	commentId: string | undefined;
	contentId: string;
	annotationState: InlineCommentPluginState | undefined;
	handleGqlError: (error: Error) => void;
};

// Using common utility function from comments-util

export function useFocusedComment({
	commentId,
	contentId,
	annotationState,
	handleGqlError,
}: FocusedCommentProps): void {
	// New comments data manager hook - only used when experiment is enabled
	const newCommentsDataResult = useCommentById({
		commentId: commentId || '',
		contentId,
		options: {
			skip: !commentId || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
			onError: handleGqlError,
		},
	});

	// Legacy query - only used when experiment is disabled
	const { data: legacyData } = useQuery<
		useFocusedCommentInlineCommentMarkerRefQuery$data,
		useFocusedCommentInlineCommentMarkerRefQuery$variables
	>(
		gql`
			query useFocusedCommentInlineCommentMarkerRefQuery(
				$commentId: ID!
				$pageId: ID!
				$contentStatus: [GraphQLContentStatus]
			) {
				comments(commentId: $commentId, pageId: $pageId, contentStatus: $contentStatus) {
					nodes {
						id
						location {
							type
							... on InlineComment {
								inlineMarkerRef
								inlineResolveProperties {
									resolved
								}
							}
						}
						parentId
					}
				}
			}
		`,
		{
			variables: {
				commentId: commentId || '',
				pageId: contentId,
				contentStatus: [GraphQLContentStatus.DRAFT, GraphQLContentStatus.CURRENT],
			},
			skip: !commentId || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
			onError: handleGqlError,
			fetchPolicy: 'cache-first',
		},
	);
	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();

	const hasOpenedFocusedComment = useRef(false);

	const { isUpdatingUnreadComments } = useUnreadCommentsState();

	// Reset hasOpenedFocusedComment when commentId changes
	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			hasOpenedFocusedComment.current = false;
		}
	}, [commentId]);

	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			return;
		}
		// Pull from comments_data_manager when experiment is enabled
		const focusedComment = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? (newCommentsDataResult.comment ?? null)
			: (legacyData?.comments?.nodes?.[0] ?? null);

		if (
			commentId &&
			focusedComment &&
			(fg('confluence_frontend_content_wrapper') || !isUpdatingUnreadComments)
		) {
			const markerRef =
				(focusedComment.location as InlineCommentMarkerRefLocation).inlineMarkerRef ?? '';
			const isResolved = Boolean(
				(focusedComment.location as InlineCommentMarkerRefLocation).inlineResolveProperties
					?.resolved,
			);

			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });

				if (markerRef) {
					setCurrentlySelectedThreadKey(markerRef);
					setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
					hasOpenedFocusedComment.current = true;
				}
			} else if (
				// dangling comment
				markerRef &&
				annotationState?.annotationsLoaded &&
				!(markerRef in annotationState.annotations) &&
				fg('confluence_frontend_new_dangling_comments_ux')
			) {
				setCurrentlySelectedThreadKey(markerRef);
				setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
				showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
				hasOpenedFocusedComment.current = true; // to prevent focused comment from being opened again. We may need a better way to handle this
			} else {
				const commentElement = document.getElementById(markerRef);

				if (commentElement && markerRef !== '') {
					// Use unified navigation for deep linking
					const success = handleDeepLinkNavigation({
						scrollBehavior: ScrollBehavior.INSTANT,
						context: NavigationContext.EDITOR,
						block: 'center',
						inline: 'nearest',
						markElement: commentElement,
						commentId,
					});

					if (success) {
						hasOpenedFocusedComment.current = true;
					}
				}
			}
		}
		return () => {
			hasOpenedFocusedComment.current = false;
		};
	}, [
		commentId,
		newCommentsDataResult.comment,
		annotationState?.annotations,
		annotationState?.annotationsLoaded,
		isUpdatingUnreadComments,
		showCommentsPanel,
		setCurrentlySelectedThreadKey,
		setCommentContainerToFocus,
		legacyData,
	]);
}
